import React, { useMemo, useState } from 'react';
import moment from 'moment';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from '../../toasts';
import creditPaymentService from '../../services/creditPayment.service';
import { getError, showCurrency } from '../../utils';
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal';
import { Section, StatStrip, DataTable, IdChip, RowActionsMenu, Button } from '../../components/EntityList';
import { useLabels } from '../../hooks/useLabels';

function formatDate(date) {
	return date ? moment.utc(date).format('DD/MM/YYYY') : '';
}

function formatMoney(currency, amount) {
	if (amount === null || amount === undefined) {
		return '';
	}
	return `${showCurrency(currency) || ''} ${Number(amount).toLocaleString('es-UY')}`.trim();
}

function CreditPaymentsCredit({ creditId, rows = [], loading, error, onRefetch }) {
	const { t } = useLabels();
	const navigate = useNavigate();
	const location = useLocation();
	const [deletingPayment, setDeletingPayment] = useState(null);

	const returnTo = () => encodeURIComponent(location.pathname + location.search);

	const handleCreatePayment = () => {
		navigate(`/AdminScreen/credits/credit/${creditId}/newCreditPayment?returnTo=${returnTo()}`);
	};

	const handleEditPayment = (payment) => {
		navigate(`/AdminScreen/creditPayments/editCreditPayment/${payment._id}?returnTo=${returnTo()}`);
	};

	const stats = useMemo(() => {
		const totalByCurrency = new Map();
		rows.forEach((payment) => {
			const key = payment.credit?.currency || '—';
			totalByCurrency.set(key, (totalByCurrency.get(key) || 0) + (payment.amount || 0));
		});
		return [
			{ label: t('entity.payment.plural'), value: rows.length },
			{
				label: 'Total cobrado',
				value:
					[...totalByCurrency.entries()].map(([currency, amount]) => formatMoney(currency, amount)).join(' · ') || '—',
				mono: true,
			},
		];
	}, [rows, t]);

	async function handleConfirmDelete() {
		try {
			await creditPaymentService.delete(deletingPayment._id);
			toast.success(t('msg.paymentDeleted'));
			onRefetch();
		} catch (err) {
			toast.error(getError(err));
		} finally {
			setDeletingPayment(null);
		}
	}

	const columns = [
		{ key: 'customId', header: t('field.id'), render: (payment) => <IdChip>{payment.customId}</IdChip> },
		{ key: 'creditDue', header: t('entity.due'), render: (payment) => payment.creditDue?.customId },
		{ key: 'date', header: t('field.date'), render: (payment) => formatDate(payment.date) },
		{
			key: 'amount',
			header: t('field.amount'),
			className: 'tw:text-right',
			render: (payment) => (
				<span className='tw:font-mono tw:tabular-nums'>{formatMoney(payment.credit?.currency, payment.amount)}</span>
			),
		},
		{ key: 'folder', header: t('field.folder'), render: (payment) => payment.folder },
	];

	const renderRowActions = (payment) => (
		<RowActionsMenu
			items={[
				{ label: t('action.edit'), onClick: () => handleEditPayment(payment) },
				payment.comprobante && {
					label: t('action.viewReceipt'),
					onClick: () => window.open(payment.comprobante.fileLink, '_blank', 'noopener,noreferrer'),
				},
				{ label: t('action.delete'), onClick: () => setDeletingPayment(payment), danger: true },
			]}
		/>
	);

	return (
		<div className='tw:flex tw:flex-col tw:gap-4'>
			<StatStrip stats={stats} />
			<div className='tw:flex tw:justify-end'>
				<Button size='sm' variant='primary' onClick={handleCreatePayment}>
					{`Nuevo ${t('entity.payment')}`}
				</Button>
			</div>
			<Section title={t('entity.payment.plural')}>
				<DataTable
					columns={columns}
					rows={rows}
					loading={loading}
					error={error}
					onRowClick={(payment) => navigate(`/AdminScreen/creditPayments/creditPayment/${payment._id}`)}
					rowActions={renderRowActions}
					emptyMessage='No se encontraron cobros para este crédito.'
				/>
			</Section>

			<ConfirmationModal
				show={Boolean(deletingPayment)}
				handleClose={() => setDeletingPayment(null)}
				handleConfirm={handleConfirmDelete}
				title={`Eliminar ${t('entity.payment')}`}
				description={t('msg.confirmDeletePayment')}
				confirmCaption={t('action.delete')}
				cancelCaption={t('action.cancel')}
			/>
		</div>
	);
}

export default CreditPaymentsCredit;
